import React, { createContext, useEffect } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { useRefs, useDatabases, useDialogs, useFirebase } from '../hooks';
import { i18n } from '../i18n';
import { isObject, parse } from '../util';
import Cookies from 'js-cookie';
// Custom imports:
import { useFirestore } from 'reactfire';

export const modifiers = [
  '\\.\\+\\d+',
  '\\.\\-\\d+',
  '\\.\\*\\d+',
  '\\.\\/\\d+',
  '\\.\\d+\\-\\d+',
  '\\.\\d+',
  '\\.tofile',
  '\\.json',
  '\\.length',
  '\\.lower',
  '\\.md5',
  '\\.trim',
  '\\.upper',
  '\\.urldecode',
  '\\.urlencode',
];

export const modifiersMap = {
  json: '.toJson()',
  length: '.length',
  lower: '.toLowerCase()',
  md5: '.toMd5()',
  tofile: '.toFile()',
  trim: '.trim()',
  upper: '.toUpperCase()',
  urldecode: '.decodeUrl()',
  urlencode: '.encodeUrl()',
};

function operate(operand, operation) {
  return eval(`operand${operation}`);
}

export const FunctionsContext = createContext({});

export const FunctionsProvider = (props) => {
  const history = useHistory();
  const { search, pathname } = useLocation();

  
  useEffect(() => {
    search
      .replace('?', '')
      .split('&')
      .filter((entry) => entry)
      .forEach((entry) => {
        const [key, value] = entry.split('=');
        set({what:`@property.${key}`,value})
      });
    const path = pathname.split('/').slice(1);
    const viewKey = path.shift();
    const viewValue = path.join('/');
    viewValue &&
      set({what:`@property.${viewKey}`,viewValue})
  }, []);


  // Custom consts:
	const { addToDatabase, deleteFromDatabase, getFromDatabase, setToDatabase } = useDatabases();
	const { hide: hideDialog, show: showDialog } = useDialogs();
	const {addToFirestore,deleteFromFirestore,getFromFirestore,updateFirestore,logInWithFirebase,logOutWithFirebase,signUpWithFirebase,resetPassword} = useFirebase();


  let { setRefs, ...refs } = useRefs();

  const handlers = {
    // Custom functions:
    replaceDatabase: async function ({ str, filter, order, limit }) {
      if (str.indexOf('@database.') !== 0) {
        return str;
      }
      try {
        return await getFromDatabase(str, { filter, order, limit });
      } catch (error) {
        console.error(error);
        return null;
      }
    },
    replaceFirestore: async function ({ str, filter, isCollection, order, limit }) {
      if (str.indexOf('@firebase.firestore.') !== 0) {
        return str;
      }
      try {
        return await getFromFirestore(str, { filter, isCollection, order, limit });
      } catch (error) {
        console.error(error);
        return null;
      }
    },
    set: async function ({ what, value }, options) {
        what = await this.replaceRefs(what, { ...options, onlyParanthesis: true });
        value = await this.replaceRefs(value, options);
        
        
        if (false) {}
        // Custom set:
        else if (what.indexOf('@database.') === 0) {
          return await setToDatabase({ what, value });
        }
        else if (what.indexOf('@firebase.firestore.') === 0) {
          return await updateFirestore({ what, value });
        }


        value = what
          .replace('@', '')
          .split('.')
          .reverse()
          .reduce((value, key) => ({ [key]: value }), value);
        const mainKey = Object.keys(value)[0];
        if (value?.app?.language) {
          i18n.changeLanguage(value.app?.language);
        }
        refs = {
          ...refs,
          [mainKey]: {
            ...refs[mainKey],
            ...value[mainKey],
          },
        };
        setRefs({ ...value });
      },
    goto: async function ({ view }, options) {
        view = await this.replaceRefs(`/${view}`, options);
        history.push(view);
      },
    do: async function ({ what }, options) {
        return await handlers.functions(what, options);
    },
    popup: async function ({ dialog }, options) {
        dialog = await this.replaceRefs(dialog, options);
        showDialog(dialog);
      },
    login: async function ({
        email,
        forgot,
        onvalid: onValid = [],
        oninvalid: onInvalid = [],
        pass,
        provider = 'email',
      }, options) {
        provider = await this.replaceRefs(provider, options);
        email = await this.replaceRefs(email, options);
        let data;

        if (forgot) {
          data = await resetPassword(email);
        } else {
          pass = await this.replaceRefs(pass, options);
          data = await logInWithFirebase({
            email,
            pass,
            provider,
          });
        }
        const { error, user: userData = {} } = data || {};
        if (error) {
          this.functions(onInvalid, options);
          return;
        }
        const { displayName, localId: id } = userData;
        const user = { displayName, email, id };
        refs = {
          ...refs,
          firebase: {
            ...refs.firebase,
            user,
          },
        };
        this.functions(onValid, options);
        return data;
      },
  
    clone: async function ({ what, into, filter, order, limit }, options = {}) {
      const value = await this.replaceRefs(what, {
        ...options,
        filter,
        order,
        limit,
        isCollection: true,
      });
      into = await this.replaceRefs(into, { ...options, onlyParanthesis: true });
      await this.set({ what: into, value }, options);
      return value;
    },
    logout: async function () {
        return await logOutWithFirebase();
      },
    add: async function ({
      what = '',
      key = '',
      to = '',
      onsuccess: onSuccess = [],
      onerror: onError = [],
    }, options = {}) {
      try {
        if (typeof what === 'string') {
          what = await this.replaceRefs(what, options);
        }
        key = await this.replaceRefs(key, options);
        to = await this.replaceRefs(to, { ...options, onlyParanthesis: true });
        const whatKeys = Object.keys(what);
        const whatValues = await Promise.all(
          whatKeys.map((whatKey) => this.replaceRefs(what[whatKey], options))
        );
        what = whatKeys.reduce(
          (what, whatKey, index) => ({ ...what, [whatKey]: whatValues[index] }),
          {}
        );
        if (false) {
        }
        // Custom add:
        else if (to.indexOf('@database.') === 0) {
            addToDatabase({ key, to, what });
        }        
        else if (to.indexOf('@firebase.firestore.') === 0) {
          addToFirestore({ key, to, what });
        }
      } catch (error) {
        return await this.functions(onError, options);
      }
      return await this.functions(onSuccess, options);
    },
    browser: async function ({ url, target = '_self' }, options) {
        const a = document.createElement('a');
        url = await this.replaceRefs(url, options);
        target = await this.replaceRefs(target, options);
        a.href = url;
        a.target = target;
        a.click();
        a.remove();
      },
    wait: async function ({ seconds }, options) {
        seconds = await this.replaceRefs(seconds, options);
        return await new Promise((resolve) =>
            setTimeout(() => resolve(), seconds * 1000)
        );
    },
    hide: async function ({ dialog }, options) {
        dialog = await this.replaceRefs(dialog, options);
        hideDialog(dialog);
      },

    functions: async function (functions = [], options) {
      for (const entry of functions) {
        const { function: f, if: _if = true, ...rest } = entry;
        const handleIf = await this.if(_if, options);
        if (_if === true || handleIf) {
          if (f === 'abort') {
            return;
          }
          await (this[f].bind(this) || (() => {}))(rest, options);
        } else {
          const { else: _else = [] } = _if;
          _else.length && (await this.functions(_else, options));
        }
      }
    },

    if: async function (
      {
        what,
        is,
        is_not,
        less_than,
        more_than,
        in: _in,
        includes,
        not_into,
        ends_with,
        starts_with,
        and,
        or,
      } = {},
      { data } = {}
    ) {
      let _what = await this.replaceRefs(what, { data });
      is =
        typeof is !== 'undefined'
          ? await this.replaceRefs(is, { data })
          : undefined;
      is_not =
        typeof is_not !== 'undefined'
          ? await this.replaceRefs(is_not, { data })
          : undefined;
      less_than =
        typeof less_than !== 'undefined'
          ? await this.replaceRefs(less_than, { data })
          : undefined;
      more_than =
        typeof more_than !== 'undefined'
          ? await this.replaceRefs(more_than, { data })
          : undefined;
      _in =
        typeof _in !== 'undefined'
          ? await this.replaceRefs(_in, { data })
          : undefined;
      includes =
        typeof includes !== 'undefined'
          ? await this.replaceRefs(includes, { data })
          : undefined;
      not_into =
        typeof not_into !== 'undefined'
          ? await this.replaceRefs(not_into, { data })
          : undefined;
      ends_with =
        typeof ends_with !== 'undefined'
          ? await this.replaceRefs(ends_with, { data })
          : undefined;
      starts_with =
        typeof starts_with !== 'undefined'
          ? await this.replaceRefs(starts_with, { data })
          : undefined;
      return (
        (((typeof is !== 'undefined' && _what === is) ||
          (typeof is_not !== 'undefined' && _what !== is_not) ||
          (typeof less_than !== 'undefined' && _what < less_than) ||
          (typeof more_than !== 'undefined' && _what > more_than) ||
          (typeof _in !== 'undefined' &&
            (_in.stringify?.() || `${_in}`).includes(_what)) ||
          (typeof includes !== 'undefined' &&
            `${_what}`.match(new RegExp(`${includes}`)) !== null) ||
          (typeof not_into !== 'undefined' &&
            `${_what}`.match(new RegExp(`${not_into}`)) === null) ||
          (typeof starts_with !== 'undefined' &&
            `${_what}`.match(new RegExp(`^${starts_with}`)) !== null) ||
          (typeof ends_with !== 'undefined' &&
            `${_what}`.match(new RegExp(`${ends_with}$`)) !== null)) &&
          (typeof and === 'undefined' || (await this.if(and, { data })))) ||
        (typeof or !== 'undefined' && (await this.if(or, { data })))
      );
    },

    get: async function (
      { what, filter, isCollection, order, limit },
      { data } = {}
    ) {
      const replaceFilterRefs = async (filter) => {
        let { what, and, or, ...condition } = filter;
        what = await this.replaceRefs(what, { data });
        let value = Object.values(condition)[0];
        value = await this.replaceRefs(value, { data });
        condition = Object.keys(condition)[0];
        let result = { ...filter, what, [condition]: value };
        if (and) {
          and = await replaceFilterRefs(and);
          result = { ...result, and };
        }
        if (or) {
          or = await replaceFilterRefs(or);
          result = { ...result, or };
        }
        return result;
      };
      const replaceOrderRefs = async (order) => {
        let { field, type } = order;
        field = await this.replaceRefs(field, { data });
        type = await this.replaceRefs(type, { data });
        return { field, type };
      };
      const replaceLimitRefs = async (limit) => {
        limit = await this.replaceRefs(limit, { data });
        return limit;
      };
      filter = filter && (await replaceFilterRefs(filter));
      order = order && (await replaceOrderRefs(order));
      limit = limit && (await replaceLimitRefs(limit));
      const value = await this.replaceRefs(what, {
        data,
        filter,
        isCollection,
        order,
        limit,
      });
      return value;
    },

    replaceRef: function (str, parenthesis, type, key) {
      const exp = new RegExp(`(${modifiers.join('|')})(\\.|$)[^.]*`);
      return key.split('.').reduce(
        function (resp, _key) {
          let operation = (`.${_key}`.match(exp)?.[0] || '').substr(1);
          key = operation ? key.replace(`.${operation}`, '') : key;
          if (operation) {
            let operand =
              typeof resp?.[_key] !== 'undefined' ? resp[_key] : resp;
            operand = typeof operand !== 'undefined' ? operand : '';
            if (operation.match(/^\d+-\d+$/)) {
              operation = `.substring(${operation.replace('-', ',')})`;
            } else if (operation.match(/^\d+$/)) {
              operation = `.charAt(${operation})`;
            } else if (operation.match(/^[+\-*/]+/)) {
              operand = operand || 0;
            }
            operation = modifiersMap[operation] || operation;
            try {
              operand = isObject(operand) ? Object.values(operand) : operand;
              return operate(operand, operation);
            } catch (e) {
            }
          }
          return typeof resp?.[_key] === 'object'
            ? resp?.[_key]
            : typeof resp?.[_key] === 'undefined'
            ? null
            : typeof resp[_key] === 'function'
            ? resp[_key]()
            : resp[_key];
        },
        { ...refs[type] }
      );
    },

    replaceRefs: async function (
      str,
      { data, filter, isCollection, order, limit, onlyParanthesis = false } = {}
    ) {
      refs.field = data;
      if (typeof str !== 'string') {
        return str;
      }

      let strWithReplacedRefs = '';
      if (data) {
        let dataStr = `${str}`.replace(
          /(^@|\(@)(field)\.([^)]+)\)?/g,
          this.replaceRef.bind(this)
        );
        if (dataStr.match(/\[object/)) {
          strWithReplacedRefs = data[str];
        } else {
          str = dataStr;
        }
      }

      if (str.indexOf('@app.timestamp') === 0) {
        return Date.now();
      }

      if (str.indexOf('@window.') === 0) {
        return eval(str.substring(1));
      }

      if (false) {
      }
      // Custom replaceRefs:
      else if (!onlyParanthesis && str.indexOf('@database.') === 0) {
        str = `${str}`.replace(
          /(\(@)([^.]+)\.([^)]+)\)?/g,
          this.replaceRef.bind(this)
        );
        const exp = new RegExp(`(${modifiers.join('|')})(\\.|$)[^.]*`);
        let operation = (str.match(exp)?.[0] || '').substr(1);
        str = operation ? str.replace(`.${operation}`, '') : str;
        let resp = await this.replaceDatabase({
          str,
          filter,
          isCollection,
          order,
          limit,
        });
        if (operation) {
          let operand = typeof resp !== 'undefined' ? resp : '';
          if (operation.match(/^\d+-\d+$/)) {
            operation = `.substring(${operation.replace('-', ',')})`;
          } else if (operation.match(/^\d+$/)) {
            operation = `.charAt(${operation})`;
          } else if (operation.match(/^[+\-*/]+/)) {
            operand = operand || 0;
          }
          operation = modifiersMap[operation] || operation;
          try {
            operand = isObject(operand) ? Object.values(operand) : operand;
            return operate(operand, operation);
          } catch (e) {
            console.error(e);
          }
        }
        return resp;
      }
      else if (
        !onlyParanthesis &&
        str.indexOf('@firebase.firestore.') === 0
      ) {
        str = `${str}`.replace(
          /(\(@)([^.]+)\.([^)]+)\)?/g,
          this.replaceRef.bind(this)
        );
        const exp = new RegExp(`(${modifiers.join('|')})(\\.|$)[^.]*`);
        let operation = (str.match(exp)?.[0] || '').substr(1);
        str = operation ? str.replace(`.${operation}`, '') : str;
        let resp = await this.replaceFirestore({ str, filter, isCollection, order, limit });
        if (operation) {
          let operand = typeof resp !== 'undefined' ? resp : '';
          if (operation.match(/^\d+-\d+$/)) {
            operation = `.substring(${operation.replace('-', ',')})`;
          } else if (operation.match(/^\d+$/)) {
            operation = `.charAt(${operation})`;
          } else if (operation.match(/^[+\-*/]+/)) {
            operand = operand || 0;
          }
          operation = modifiersMap[operation] || operation;
          try {
            operand = isObject(operand) ? Object.values(operand) : operand;
            return operate(operand, operation);
          } catch (e) {
            console.error(e);
          }
        }
        return resp;
      }

      strWithReplacedRefs = `${str}`.replace(
        onlyParanthesis
          ? /(\(@)([^.]+)\.([^)]+)\)?/g
          : /(^@|\(@)([^.]+)\.([^)]+)\)?/g,
        this.replaceRef.bind(this)
      );

      if( typeof strWithReplacedRefs !== 'string') {
        return strWithReplacedRefs;
      }

      if (strWithReplacedRefs.match(/\[object/)) {
        const replacedRef = str
          .substring(1)
          .split('.')
          .reduce(
            (resp, key) => {
              if (!resp) {
                return resp;
              }
              return typeof resp?.[key] === 'undefined'
                ? null
                : typeof resp[key] === 'function'
                ? resp[key]()
                : resp[key];
            },
            { ...refs }
          );
        return replacedRef;
      }

      strWithReplacedRefs = parse(strWithReplacedRefs);
      return Promise.resolve(strWithReplacedRefs);
    },
  };

  const get = async (props, options) => {
    return await handlers.get?.(props, options);
  };

  const _if = async (props, options) => {
    return await handlers.if(props, options);
  };

  const set = async (props, options) => {
    return await handlers.set?.(props, options);
  };

  const run = async (_functions, options) => {
    return await handlers.functions(_functions, options);
  };

  return (
    <FunctionsContext.Provider
      value={{
        run,
        get,
        if: _if,
        set,
      }}
    >
      {props.children}
    </FunctionsContext.Provider>
  );
};

export const FunctionsConsumer = FunctionsContext.Consumer;
export default FunctionsContext;
